/*
 * Copyright (c) 2023 Bolmate B.V.
 */

interface DefaultSystemOptions {
    tinyApiKey: string;
    maintenanceMode: boolean;
    bolOauthClientId: string;
    hideProductSyncForUsers: Array<string>;
}

export interface SystemOptions extends DefaultSystemOptions {
    platformUrl: string;
    sentryUrl: string | null;
    gaID: string | null;
    sendToGaID: string | null;
    mode: 'prod' | 'test' | 'dev';
    filePrefix: string;
    allowExperimental: boolean;
    stripeApiKey: string;
    appUrl: string;
    oauthState: string | null;
    showDevUpdates?: 'none' | 'all' | 'regular';
}

interface Settings extends Record<string, SystemOptions> {
    prod: SystemOptions;
    dev: SystemOptions;
    accept: SystemOptions;
    comet: SystemOptions;
    orbit: SystemOptions;
}

const defaultSettings: DefaultSystemOptions = {
    tinyApiKey: '0pdhayykjtfwu6s03da4co7s0o4jxohguk6wq2cxgs6k6mh6',
    maintenanceMode: false,
    bolOauthClientId: 'e2f76440-cd8f-4c62-98ad-acfbc1a7cb8b',
    hideProductSyncForUsers: ['2abaebcf-942e-4b6c-880a-145c1ae0a6b0', 'b14032d9-5bae-4f8b-95cc-dc5665f0aa38']
};

const appSettings: Settings = {
    prod: {
        ...defaultSettings,
        mode: 'prod',
        platformUrl: 'https://app.bolmate.nl/bolmate-core/v1/',
        gaID: 'G-TNDPTEQKHY',
        sendToGaID: 'G-DLK9PDZ6EY',
        filePrefix: 'upload/',
        appUrl: 'https://app.bolmate.nl',
        oauthState: null,
        stripeApiKey:
            'pk_live_51Hj5gkJJllIUmlpmGNi7jzYiZosHz5I13UXxRkNop14URxcTrrITAhH7OnRx3lPWBVgQzmQ66ywm4QVDUQJJoJgH00oRVqIjmU',
        sentryUrl: 'https://d032b45f225de0d9d0e6563c362acf20@o437233.ingest.sentry.io/4506546501844992',
        allowExperimental: false
    },
    accept: {
        ...defaultSettings,
        mode: 'test',
        platformUrl: 'https://staging.bolmate.nl/bolmate-core/v1/',
        filePrefix: 'upload/',
        sentryUrl: 'https://55421af56e0747e5b2efcf8a40384b71@o437233.ingest.sentry.io/5399518',
        allowExperimental: false,
        stripeApiKey: '***REMOVED***',
        appUrl: 'https://staging.bolmate.nl',
        gaID: null,
        sendToGaID: null,
        oauthState: '6ce8458b111144c0ab9cd8a3f5dcc4b0'
    },
    comet: {
        ...defaultSettings,
        mode: 'test',
        platformUrl: 'https://comet.bolmate.nl/bolmate-core/v1/',
        filePrefix: 'upload/',
        sentryUrl: 'https://55421af56e0747e5b2efcf8a40384b71@o437233.ingest.sentry.io/5399518',
        allowExperimental: false,
        stripeApiKey: '***REMOVED***',
        appUrl: 'https://comet.bolmate.nl',
        gaID: null,
        sendToGaID: null,
        oauthState: '6ce8458b111144c0ab9cd8a3f5dcc4b0'
    },
    orbit: {
        ...defaultSettings,
        mode: 'test',
        platformUrl: 'https://orbit.bolmate.nl/bolmate-core/v1/',
        filePrefix: 'upload/',
        sentryUrl: 'https://55421af56e0747e5b2efcf8a40384b71@o437233.ingest.sentry.io/5399518',
        allowExperimental: false,
        stripeApiKey: '***REMOVED***',
        appUrl: 'https://orbit.bolmate.nl',
        gaID: null,
        sendToGaID: null,
        oauthState: '6ce8458b111144c0ab9cd8a3f5dcc4b0'
    },
    dev: {
        ...defaultSettings,
        mode: 'dev',
        // Using the hostname, we enable connecting to the application running on another local device
        platformUrl: `http://${window.location.hostname}:30377/v1/`,
        filePrefix: 'https://app.bolmate.nl/upload/',
        allowExperimental: true,
        stripeApiKey: '***REMOVED***',
        appUrl: 'http://127.0.0.1:1337',
        showDevUpdates: 'regular',
        gaID: null,
        sendToGaID: null,
        oauthState: null,
        sentryUrl: null
    }
};

const mode = window.mode || 'prod';
window.allowCookies = true;
const windowSettings = window.settings ?? {};
window.settings = {...appSettings[mode], ...windowSettings};
